import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  GeneratedSignal,
  SignalHistoryRow,
  SignalHistoryStatus,
} from "../api/dataSource";
import { SignalTradeTicketModal } from "../components/SignalTradeTicketModal";
import { SymbolLabel } from "../components/SymbolLabel";
import { fmtPx } from "../lib/format";
import { gateReasonLabel, isWatchOnlyReason } from "../lib/gateReasons";
import type { TradePreview } from "../types";

type Props = {
  listSignalHistory: (limit?: number) => Promise<SignalHistoryRow[]>;
  onOpenApproval: (approvalId: string) => void;
  onRepriceSignal: (
    signal: GeneratedSignal,
    riskDollars?: string,
  ) => Promise<string>;
  onPreviewSignal: (
    signal: GeneratedSignal,
    riskDollars?: string,
  ) => Promise<{
    preview: TradePreview;
    actionable: boolean;
    issues: string[];
  }>;
};

type Filter = "all" | SignalHistoryStatus;
// Phase 9-UI: client-side asset-class filter, orthogonal to status.
type ClassFilter = "all" | "equity" | "crypto" | "forex";
const classFilters: ClassFilter[] = ["all", "equity", "crypto", "forex"];

const filters: Filter[] = [
  "all",
  "pending",
  "skipped",
  "expired",
  "approved",
  "placed",
];

const statusLabel: Record<SignalHistoryStatus, string> = {
  skipped: "not approved",
  pending: "pending",
  expired: "expired",
  approved: "approved",
  placed: "placed",
};

const filterLabel = (filter: Filter): string =>
  filter === "all" ? "all" : statusLabel[filter];

const statusKind = (status: SignalHistoryStatus): string => {
  if (status === "placed" || status === "approved") return "ok";
  if (status === "pending") return "info";
  if (status === "expired") return "warn";
  return "idle";
};

const fmtGeneratedAt = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const planForSignal = (signal: SignalHistoryRow): string => {
  const target = signal.targets[0];
  return [
    fmtPx(signal.entry),
    " stop ",
    fmtPx(signal.stop),
    target === undefined ? "" : ` -> ${fmtPx(target)}`,
  ].join("");
};

const shortId = (id: string | null): string => {
  if (id === null) return "none";
  return id.length > 12 ? `${id.slice(0, 8)}...${id.slice(-4)}` : id;
};

const historyAsSignal = (row: SignalHistoryRow): GeneratedSignal => ({
  signalId: row.signalId,
  symbol: row.symbol,
  companyName: row.companyName,
  strategyId: row.strategyId,
  assetClass: row.assetClass ?? "equity",
  barDate: row.barDate,
  direction: row.direction,
  entry: row.entry,
  stop: row.stop,
  targets: row.targets,
  score: row.score,
  factors: row.rationale ? [row.rationale] : [],
  rrRatio: row.rrRatio,
  aiConfidence: null,
  aiRationale: row.rationale,
  aiRiskFlags: [],
  aiModelId: row.aiModelId,
  researchEnrichment: row.researchEnrichment,
  edgeScore: row.edgeScore,
  regimeFit: row.regimeFit,
  portfolioFit: row.portfolioFit,
  approvalGateReason: row.approvalGateReason,
  signalRole: row.signalRole,
});

export const SignalHistoryScreen = ({
  listSignalHistory,
  onOpenApproval,
  onRepriceSignal,
  onPreviewSignal,
}: Props) => {
  const [rows, setRows] = useState<SignalHistoryRow[]>([]);
  const [filter, setFilter] = useState<Filter>("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ticketRow, setTicketRow] = useState<SignalHistoryRow | null>(null);
  const [queueingSignalId, setQueueingSignalId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await listSignalHistory(500));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load signal history");
    } finally {
      setLoading(false);
    }
  }, [listSignalHistory]);

  useEffect(() => {
    void load();
  }, [load]);

  const [classFilter, setClassFilter] = useState<ClassFilter>("all");

  const visibleRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          (filter === "all" || row.approvalStatus === filter) &&
          (classFilter === "all" ||
            (row.assetClass ?? "equity") === classFilter),
      ),
    [classFilter, filter, rows],
  );

  const queueApproval = useCallback(
    async (row: SignalHistoryRow, riskDollars: string) => {
      setQueueingSignalId(row.signalId);
      setError(null);
      try {
        const approvalId = await onRepriceSignal(
          historyAsSignal(row),
          riskDollars,
        );
        setTicketRow(null);
        await load();
        onOpenApproval(approvalId);
        return approvalId;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Approval queue failed");
        throw e;
      } finally {
        setQueueingSignalId(null);
      }
    },
    [load, onOpenApproval, onRepriceSignal],
  );

  return (
    <div data-screen-label="Signals">
      <div className="tp-page-head">
        <h1>Signals</h1>
        <div className="meta">
          {loading ? "loading" : `${visibleRows.length} of ${rows.length} rows`}
        </div>
      </div>

      <div className="tp-actionbar">
        {filters.map((id) => (
          <button
            key={id}
            type="button"
            className={"tp-chip-btn" + (filter === id ? " active" : "")}
            onClick={() => setFilter(id)}
          >
            {filterLabel(id)}
          </button>
        ))}
        <span
          style={{
            width: 1,
            alignSelf: "stretch",
            background: "var(--tp-ink-5)",
            margin: "0 4px",
          }}
        />
        {classFilters.map((id) => (
          <button
            key={`class-${id}`}
            type="button"
            className={"tp-chip-btn" + (classFilter === id ? " active" : "")}
            onClick={() => setClassFilter(id)}
          >
            {id}
          </button>
        ))}
        <button
          type="button"
          className="tp-btn tp-btn-secondary tp-btn-sm"
          disabled={loading}
          onClick={() => void load()}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
        {error && <span className="tp-inline-status bad">{error}</span>}
      </div>

      <div className="tp-tile">
        <div className="tp-tile-head">
          <span className="label">Generated signal history</span>
          <span className="tp-muted">latest 500</span>
        </div>
        <div className="tp-tile-body" style={{ padding: 0 }}>
          <div className="tp-table-scroll">
            <table className="tp-table tp-signal-history-table">
              <thead>
                <tr>
                  <th>Generated</th>
                  <th>Symbol</th>
                  <th>Strategy</th>
                  <th>Bar</th>
                  <th className="center">Score</th>
                  <th className="center">Edge</th>
                  <th>Plan</th>
                  <th>Gate</th>
                  <th>Status</th>
                  <th className="right">Approval</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="tp-empty-cell">
                      {loading ? "Loading signal history..." : "No generated signals."}
                    </td>
                  </tr>
                ) : (
                  visibleRows.map((signal) => {
                    const approvalId = signal.approvalId;
                    const canQueue =
                      signal.approvalStatus === "skipped" ||
                      signal.approvalStatus === "expired";
                    return (
                      <tr key={signal.signalId}>
                        <td className="num">
                          {fmtGeneratedAt(signal.generatedAt)}
                        </td>
                        <td className="ticker">
                          <SymbolLabel
                            symbol={signal.symbol}
                            companyName={signal.companyName}
                          />
                        </td>
                        <td className="num">
                          {signal.strategyId}
                          {signal.signalRole === "hedge_watch" && (
                            <span className="tp-pill muted">hedge</span>
                          )}
                        </td>
                        <td className="num">{signal.barDate}</td>
                        <td className="center num">{signal.score}</td>
                        <td className="center num">
                          {signal.edgeScore ?? "—"}
                        </td>
                        <td className="num">{planForSignal(signal)}</td>
                        <td
                          className={
                            isWatchOnlyReason(signal.approvalGateReason)
                              ? "tp-muted"
                              : "num"
                          }
                        >
                          {gateReasonLabel(signal.approvalGateReason)}
                        </td>
                        <td>
                          <span
                            className={`tp-status tp-status-compact ${statusKind(
                              signal.approvalStatus,
                            )}`}
                          >
                            <span className="dot" />
                            {statusLabel[signal.approvalStatus]}
                          </span>
                        </td>
                        <td className="right">
                          {signal.approvalStatus === "pending" &&
                          approvalId !== null ? (
                            <button
                              type="button"
                              className="tp-btn tp-btn-secondary tp-btn-sm"
                              onClick={() => onOpenApproval(approvalId)}
                            >
                              Review
                            </button>
                          ) : canQueue ? (
                            <button
                              type="button"
                              className="tp-btn tp-btn-secondary tp-btn-sm"
                              disabled={queueingSignalId === signal.signalId}
                              onClick={() => setTicketRow(signal)}
                            >
                              {queueingSignalId === signal.signalId
                                ? "Queueing..."
                                : "Review"}
                            </button>
                          ) : (
                            <span className="num flat">
                              {shortId(approvalId)}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {ticketRow && (
        <SignalTradeTicketModal
          signal={historyAsSignal(ticketRow)}
          onClose={() => setTicketRow(null)}
          onPreview={onPreviewSignal}
          onConfirm={(riskDollars) => queueApproval(ticketRow, riskDollars)}
        />
      )}
    </div>
  );
};
